import { useEffect, useRef, useState, useCallback } from 'react';

const WS_RECONNECT_DELAY = 3000; // 3 seconds
const WS_HEARTBEAT_INTERVAL = 30000; // 30 seconds

/**
 * Custom hook for WebSocket connection to Orders/Provisioning updates
 * Handles connection, reconnection, and message routing
 */
export function useOrderWebSocket(onMessage) {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const wsRef = useRef(null);
  const heartbeatRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const subscribedOrdersRef = useRef(new Set());
  const onMessageRef = useRef(onMessage);

  // Keep onMessage ref updated
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  const getWebSocketUrl = useCallback(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    // Convert http(s) to ws(s)
    const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
    const wsHost = backendUrl.replace(/^https?:\/\//, '');
    
    // No token in URL — browser will automatically send the HttpOnly cookie
    // (same-origin WebSocket connections include cookies per RFC 6455)
    return `${wsProtocol}://${wsHost}/api/ws/orders`;
  }, []);

  const connect = useCallback(() => {
    const url = getWebSocketUrl();
    if (!url) {
      console.log('[WS-Orders] Could not build WebSocket URL, skipping');
      return;
    }

    // Close existing connection if any
    if (wsRef.current) {
      wsRef.current.close();
    }

    try {
      console.log('[WS-Orders] Connecting to:', url.split('?')[0]);
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('[WS-Orders] Connected successfully');
        setIsConnected(true);
        setConnectionError(null);

        // Start heartbeat
        heartbeatRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ action: 'ping' }));
          }
        }, WS_HEARTBEAT_INTERVAL);

        // Re-subscribe to any previously subscribed orders
        subscribedOrdersRef.current.forEach(orderId => {
          ws.send(JSON.stringify({ action: 'subscribe_order', order_id: orderId }));
        });
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          // Handle pong silently
          if (data.type === 'pong') return;
          
          // Handle connection confirmation
          if (data.type === 'connected') {
            console.log('[WS-Orders] Connection confirmed:', data.message);
            return;
          }

          // Handle subscription confirmations
          if (data.type === 'subscribed') {
            console.log('[WS-Orders] Subscribed to order:', data.order_id);
            return;
          }

          // Forward other messages to handler via ref
          if (onMessageRef.current) {
            onMessageRef.current(data);
          }
        } catch (error) {
          console.error('[WS-Orders] Failed to parse message:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('[WS-Orders] WebSocket error:', error);
        setConnectionError('Connection error');
      };

      ws.onclose = (event) => {
        console.log('[WS-Orders] Connection closed:', event.code, event.reason);
        setIsConnected(false);
        
        // Clear heartbeat
        if (heartbeatRef.current) {
          clearInterval(heartbeatRef.current);
          heartbeatRef.current = null;
        }

        // Attempt to reconnect unless closed intentionally or due to auth failure
        // 1000 = clean close, 4001 = auth rejected (proper WS frame),
        // 1006 = abnormal close (HTTP 403 from pre-accept rejection — same cause, don't retry)
        if (event.code !== 1000 && event.code !== 4001 && event.code !== 1006) {
          console.log('[WS-Orders] Scheduling reconnect...');
          reconnectTimeoutRef.current = setTimeout(connect, WS_RECONNECT_DELAY);
        } else if (event.code === 1006) {
          console.warn('[WS-Orders] Auth/network error (1006), not reconnecting automatically');
        }
      };
    } catch (error) {
      console.error('[WS-Orders] Failed to create WebSocket:', error);
      setConnectionError('Failed to connect');
    }
  }, [getWebSocketUrl]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
    
    if (wsRef.current) {
      wsRef.current.close(1000, 'Intentional disconnect');
      wsRef.current = null;
    }
    
    setIsConnected(false);
    subscribedOrdersRef.current.clear();
  }, []);

  const subscribeToOrder = useCallback((orderId) => {
    subscribedOrdersRef.current.add(orderId);
    
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        action: 'subscribe_order',
        order_id: orderId
      }));
    }
  }, []);

  const unsubscribeFromOrder = useCallback((orderId) => {
    subscribedOrdersRef.current.delete(orderId);
    
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        action: 'unsubscribe_order',
        order_id: orderId
      }));
    }
  }, []);

  // Connect on mount
  useEffect(() => {
    connect();
    
    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  return {
    isConnected,
    connectionError,
    subscribeToOrder,
    unsubscribeFromOrder,
    reconnect: connect
  };
}

export default useOrderWebSocket;
